import type { TFilterConfig } from "@components/DataTable/types"
import type { TCaseFilters } from "@apis/cases/types"
import {
  useGetCaseChannelsQuery,
  useGetCaseStatusChangeReasonsQuery,
  useGetCaseStatusesQuery,
  useGetCaseTypeCategoriesQuery,
  useGetCaseTypesQuery,
} from "@apis/cases/useCases"
import { useGetCaseAssignmentGroupsQuery } from "@apis/shared/useShared"
import { useGetMarketsQuery } from "@apis/offers/useOffers"
import { useServerFilters } from "@hooks/useServerFilters"
import { useAuthUserDetails } from "@apis/users/useUsers"
import { defaultDateRangePresets } from "@components/SelectDateRange/presets"
import { dateRangeToISO } from "@components/SelectDateRange/utils"
import type { TDateRangeISO } from "@components/SelectDateRange/types"

const DEFAULT_CASE_FILTERS: TCaseFilters = {
  case_type: "roles",
  case_status_name: ["New", "Changes Pending", "Working", "Triage"],
  date_range: dateRangeToISO(
    defaultDateRangePresets
      .find((preset) => preset.default)
      ?.value(),
  ),
}

export const useCasesFilters = () => {
  const getAuthUserDetailsQuery = useAuthUserDetails()
  const casesFilters = useServerFilters<TCaseFilters>(DEFAULT_CASE_FILTERS)
  const filters = casesFilters.serverFilters

  const getCaseTypesQuery = useGetCaseTypesQuery()
  const getCaseTypeCategoriesQuery = useGetCaseTypeCategoriesQuery()
  const getCaseAssignmentGroupsQuery = useGetCaseAssignmentGroupsQuery()
  const getCaseChannelsQuery = useGetCaseChannelsQuery()
  const getMarketsQuery = useGetMarketsQuery()
  const getCaseStatusesQuery = useGetCaseStatusesQuery()
  const getCaseStatusChangeReasonsQuery = useGetCaseStatusChangeReasonsQuery()

  const filterObj = {
    case_type_description: filters.case_type_description,
    category_name: filters.category_name,
    channel_name: filters.channel_name,
    market_code: filters.market_code,
    case_status_name: filters.case_status_name,
    case_status_change_reason_desc: filters.case_status_change_reason_desc,
    case_description: filters.case_description,
    last_note: filters.last_note,
    email: filters.email,
    assigned_to_user_name: filters.assigned_to_user_name,
  }

  const dateRangeToApiParams = (
    isoRange: TDateRangeISO | undefined,
  ): { custom_date_from?: string; custom_date_to?: string } => {
    if (!isoRange?.from || !isoRange?.to) return {}

    return {
      custom_date_from: isoRange.from,
      custom_date_to: isoRange.to,
    }
  }

  const casesApiParams = {
    filter_by_roles: true,
    case_type: filters.case_type,
    filterObj,
    order: "desc" as const,
    show_cancelled: true,
    show_closed: true,
    show_new: true,
    show_pending: true,
    show_triage: true,
    show_working: true,
    sort_column: "case_created_dt",
    user_id: getAuthUserDetailsQuery.data?.user_id,
    ...dateRangeToApiParams(filters.date_range),
  }

  const filterConfig: TFilterConfig[] = [
    {
      type: "select",
      id: "case_type",
      placeholder: "Select Group",
      defaultValue: DEFAULT_CASE_FILTERS.case_type,
      options: getCaseAssignmentGroupsQuery.data,
    },

    {
      type: "selectMultiple",
      id: "case_status_name",
      placeholder: "Select Statuses",
      defaultValue: DEFAULT_CASE_FILTERS.case_status_name,
      options: getCaseStatusesQuery.data,
    },
    {
      type: "dateRange",
      id: "date_range",
      placeholder: "Select Date",
      defaultValue: DEFAULT_CASE_FILTERS.date_range,
    },
    {
      type: "selectMultiple",
      id: "case_type_description",
      placeholder: "Select Tiers",
      defaultValue: null,
      options: getCaseTypesQuery.data,
    },
    {
      type: "selectMultiple",
      id: "category_name",
      placeholder: "Select Reasons",
      defaultValue: null,
      options: getCaseTypeCategoriesQuery.data,
    },
    {
      type: "selectMultiple",
      id: "channel_name",
      placeholder: "Select Channels",
      defaultValue: null,
      options: getCaseChannelsQuery.data,
    },
    {
      type: "selectMultiple",
      id: "market_code",
      placeholder: "Select Markets",
      defaultValue: null,
      options: getMarketsQuery.data,
    },
    {
      type: "selectMultiple",
      id: "case_status_change_reason_desc",
      placeholder: "Select Dispositions",
      defaultValue: null,
      options: getCaseStatusChangeReasonsQuery.data,
    },
    {
      type: "text",
      id: "case_description",
      placeholder: "Search Description",
      defaultValue: null,
    },
    {
      type: "text",
      id: "last_note",
      placeholder: "Search Last Note",
      defaultValue: null,
    },
    {
      type: "text",
      id: "email",
      placeholder: "Search Created By",
      defaultValue: null,
    },
    {
      type: "text",
      id: "assigned_to_user_name",
      placeholder: "Search Assigned To",
      defaultValue: null,
    },
  ]

  return {
    casesFilters,
    filterConfig,
    casesApiParams,
  }
}
